import { skipToken } from '@reduxjs/toolkit/query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@shared/ui';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import type {
  AdminAppointmentMutationResult,
  AdminCancellationInitiator,
  AppointmentMutationScope,
} from '../appointments/types';
import {
  adminAppointmentErrorMessage,
  isAdminAppointmentTerminalRejection,
} from './adminAppointmentErrors';
import {
  useCancelAdminAppointmentMutation,
  useGetAdminAppointmentQuery,
  useGetAdminAppointmentSchedulingContextQuery,
  useRescheduleAdminAppointmentMutation,
} from './api/adminAppointmentsApi';
import { useAdminAppointmentOperation } from './useAdminAppointmentOperation';
import { useStableIdempotencyKey } from './useStableIdempotencyKey';

interface AdminAppointmentActionsDialogProps {
  open: boolean;
  appointmentId: string;
  viewerTimezone: string;
  onClose: () => void;
  onComplete: () => void;
}

type Mode = 'detail' | 'reschedule' | 'cancel';

export function AdminAppointmentActionsDialog({
  open,
  appointmentId,
  viewerTimezone,
  onClose,
  onComplete,
}: AdminAppointmentActionsDialogProps) {
  const adminPrincipalId = useAppSelector((state) => {
    const principalId = state.auth.user?.id;
    if (!principalId) throw new Error('authenticated admin principal is required');
    return principalId;
  });
  const [mode, setMode] = useState<Mode>('detail');
  const [visibleWeeks, setVisibleWeeks] = useState<1 | 2 | 3>(1);
  const [scope, setScope] = useState<AppointmentMutationScope>('appointment');
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [initiator, setInitiator] = useState<AdminCancellationInitiator | null>(null);
  const [opsNote, setOpsNote] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [confirmedComplete, setConfirmedComplete] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [requestUnresolved, setRequestUnresolved] = useState(false);
  const [terminalConfirmation, setTerminalConfirmation] = useState<string | null>(null);
  const submitting = useRef(false);
  const completedOperation = useRef<string | null>(null);
  const rescheduleIdempotency = useStableIdempotencyKey('admin-reschedule', adminPrincipalId);
  const cancelIdempotency = useStableIdempotencyKey('admin-cancel', adminPrincipalId);
  const appointmentQuery = useGetAdminAppointmentQuery(open ? appointmentId : skipToken);
  const appointment = appointmentQuery.data;
  const needsSchedulingContext = Boolean(
    appointment && (mode === 'reschedule' || (mode === 'cancel' && appointment.kind === 'coaching'))
  );
  const schedulingQuery = useGetAdminAppointmentSchedulingContextQuery(
    needsSchedulingContext && appointment
      ? {
          kind: appointment.kind,
          coach_person_id: appointment.expected_identities.coach.person_id,
          member_person_id: appointment.expected_identities.member.person_id,
          viewer_timezone: viewerTimezone,
          visible_weeks: visibleWeeks,
          intent: 'reschedule',
          appointment_id: appointment.id,
        }
      : skipToken
  );
  const [reschedule, rescheduleState] = useRescheduleAdminAppointmentMutation();
  const [cancel, cancelState] = useCancelAdminAppointmentMutation();
  const operationQuery = useAdminAppointmentOperation(operationId, [appointmentId]);
  const operation = operationQuery.data;

  useEffect(() => {
    if (
      !operation ||
      operation.status === 'pending' ||
      completedOperation.current === operation.id
    ) {
      return;
    }
    completedOperation.current = operation.id;
    submitting.current = false;
    setRequestUnresolved(false);
    rescheduleIdempotency.markTerminal();
    cancelIdempotency.markTerminal();
    if (operation.status === 'confirmed') {
      setConfirmedComplete(true);
      setMessage(terminalConfirmation ?? 'Scheduling changes are confirmed.');
      onComplete();
    } else {
      setErrorMessage(adminAppointmentErrorMessage(undefined, operation.rejection));
    }
  }, [cancelIdempotency, onComplete, operation, rescheduleIdempotency, terminalConfirmation]);

  if (appointmentQuery.isLoading) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent>
          <CircularProgress size={22} />
        </DialogContent>
      </Dialog>
    );
  }
  if (!appointment) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Appointment details</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            <Typography>Appointment details could not be loaded.</Typography>
            <Typography variant="body2">
              Retry, or close without making changes. No scheduling changes were made.
            </Typography>
            <Button variant="outlined" onClick={() => appointmentQuery.refetch()} sx={{ mt: 1 }}>
              Retry
            </Button>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  const currentAppointment = appointment;

  const context = schedulingQuery.data;
  const schedulingContextUnavailable = Boolean(
    needsSchedulingContext && !schedulingQuery.isLoading && (schedulingQuery.error || !context)
  );
  const scopes = safeScopes(
    mode === 'reschedule'
      ? (context?.reschedule_target?.allowed_scopes ??
          appointment.action_capabilities.reschedule.scopes)
      : appointment.action_capabilities.cancel.scopes
  );
  const schedulingWarnings = Array.isArray(context?.warnings) ? context.warnings : [];
  const nearTermCoaching = schedulingWarnings.includes('near_term_coaching_reschedule');
  const confirmationRequired =
    mode === 'reschedule'
      ? nearTermCoaching
      : Boolean(appointment.action_capabilities.cancel.near_term_confirmation_required);
  const effectiveScope = scopes.includes(scope) ? scope : (scopes[0] ?? 'appointment');
  const seriesMutationVersion = context?.reschedule_target?.series_mutation_version;
  const seriesGuardReady =
    !appointment.series_id ||
    (Number.isInteger(seriesMutationVersion) && Number(seriesMutationVersion) > 0);
  const cancellationValid =
    (!appointment.action_capabilities.cancel.initiator_required || initiator !== null) &&
    (!appointment.action_capabilities.cancel.ops_note_required || opsNote.trim().length > 0) &&
    (!appointment.action_capabilities.cancel.reason_required || reason.trim().length > 0) &&
    (!confirmationRequired || confirmed) &&
    seriesGuardReady &&
    scopes.length > 0;
  const unchanged = !selectedStart || selectedStart === appointment.current_schedule.start_at;
  const pending =
    Boolean(operationId) &&
    (!operation || operation.id !== operationId || operation.status === 'pending');
  const locked = requestUnresolved || rescheduleState.isLoading || cancelState.isLoading || pending;

  async function handleReschedule() {
    const target = context?.reschedule_target;
    if (
      unchanged ||
      scopes.length === 0 ||
      !target ||
      !seriesGuardReady ||
      (confirmationRequired && !confirmed) ||
      submitting.current
    )
      return;
    submitting.current = true;
    setRequestUnresolved(true);
    setErrorMessage(null);
    const commandInputs = {
      contract_version: 'appointment-admin-reschedule.v1' as const,
      coach_person_id: currentAppointment.expected_identities.coach.person_id,
      member_person_id: currentAppointment.expected_identities.member.person_id,
      expected_version: target.version,
      expected_series_mutation_version: target.series_id ? target.series_mutation_version : null,
      scope: effectiveScope,
      start_at: selectedStart as string,
      viewer_timezone: context.timezone,
    };
    try {
      const result = await reschedule({
        appointmentId: currentAppointment.id,
        command: {
          ...commandInputs,
          idempotency_key: rescheduleIdempotency.keyFor({
            ...commandInputs,
            appointment_id: currentAppointment.id,
          }),
        },
      }).unwrap();
      handleMutationResult(result);
    } catch (error) {
      submitting.current = false;
      setRequestUnresolved(false);
      if (isAdminAppointmentTerminalRejection(error)) rescheduleIdempotency.markTerminal();
      setErrorMessage(adminAppointmentErrorMessage(error));
    }
  }

  async function handleCancel() {
    const target = context?.reschedule_target;
    if (!cancellationValid || !initiator || submitting.current) return;
    let expectedVersion = currentAppointment.version;
    if (currentAppointment.kind === 'coaching') {
      if (!target) return;
      expectedVersion = target.version;
    }
    submitting.current = true;
    setRequestUnresolved(true);
    setErrorMessage(null);
    const commandInputs = {
      contract_version: 'appointment-admin-cancel.v1' as const,
      coach_person_id: currentAppointment.expected_identities.coach.person_id,
      member_person_id: currentAppointment.expected_identities.member.person_id,
      expected_version: expectedVersion,
      expected_series_mutation_version: currentAppointment.series_id
        ? (seriesMutationVersion ?? null)
        : null,
      scope: effectiveScope,
      initiator,
      ops_note: opsNote.trim(),
      reason: reason.trim() || null,
      near_term_chemistry_confirmed: Boolean(currentAppointment.kind === 'chemistry' && confirmed),
    };
    try {
      const result = await cancel({
        appointmentId: currentAppointment.id,
        command: {
          ...commandInputs,
          idempotency_key: cancelIdempotency.keyFor({
            ...commandInputs,
            appointment_id: currentAppointment.id,
          }),
        },
      }).unwrap();
      handleMutationResult(result);
    } catch (error) {
      submitting.current = false;
      setRequestUnresolved(false);
      if (isAdminAppointmentTerminalRejection(error)) cancelIdempotency.markTerminal();
      setErrorMessage(adminAppointmentErrorMessage(error));
    }
  }

  function handleMutationResult(result: AdminAppointmentMutationResult) {
    const returnedSchedule = Array.isArray(result.appointments)
      ? result.appointments[0]?.current_schedule
      : undefined;
    const confirmedStart = returnedSchedule?.start_at ?? selectedStart;
    const confirmedTimezone = returnedSchedule?.timezone ?? context?.timezone ?? viewerTimezone;
    if (result.status === 'pending') {
      setOperationId(result.operation_id);
      if (mode === 'reschedule' && confirmedStart) {
        setTerminalConfirmation(
          `Rescheduled to ${formatInstant(confirmedStart, confirmedTimezone)} (${confirmedTimezone}).`
        );
      } else if (mode === 'cancel') {
        const affectedCount =
          effectiveScope === 'this_and_following'
            ? result.appointments.length || context?.active_series?.remaining_appointment_count || 1
            : 1;
        setTerminalConfirmation(
          `Cancelled ${affectedCount} ${affectedCount === 1 ? 'session' : 'sessions'}.`
        );
      }
      setMessage('Scheduling changes are pending provider confirmation.');
      return;
    }
    if (result.status === 'rejected') {
      submitting.current = false;
      setRequestUnresolved(false);
      rescheduleIdempotency.markTerminal();
      cancelIdempotency.markTerminal();
      setErrorMessage(adminAppointmentErrorMessage(undefined, result.rejection));
      return;
    }
    submitting.current = false;
    setRequestUnresolved(false);
    rescheduleIdempotency.markTerminal();
    cancelIdempotency.markTerminal();
    if (mode === 'cancel') {
      const affectedCount =
        effectiveScope === 'this_and_following'
          ? result.appointments.length || context?.active_series?.remaining_appointment_count || 1
          : 1;
      setMessage(`Cancelled ${affectedCount} ${affectedCount === 1 ? 'session' : 'sessions'}.`);
    } else if (
      Array.isArray(result.warnings) &&
      result.warnings.includes('near_term_coaching_reschedule')
    ) {
      setMessage(
        `Rescheduled to ${formatInstant(confirmedStart as string, confirmedTimezone)} (${confirmedTimezone}). Participants may need immediate notice.`
      );
    } else if (mode === 'reschedule') {
      setMessage(
        `Rescheduled to ${formatInstant(confirmedStart as string, confirmedTimezone)} (${confirmedTimezone}).`
      );
    } else {
      setMessage('Scheduling changes are confirmed.');
    }
    setConfirmedComplete(true);
    onComplete();
  }

  return (
    <Dialog open={open} onClose={locked ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{dialogTitle(mode)}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography>{`${appointment.expected_identities.coach.display_name} and ${appointment.expected_identities.member.display_name}`}</Typography>
        <Typography>{identityLabel('Coach', appointment.expected_identities.coach)}</Typography>
        <Typography>{identityLabel('Member', appointment.expected_identities.member)}</Typography>
        <Typography color="text.secondary">
          Google Meet · {appointmentDurationMinutes(appointment)} minutes ·{' '}
          {appointment.current_schedule.timezone}
        </Typography>
        {appointment.meeting.join_url && (
          <a href={appointment.meeting.join_url} target="_blank" rel="noreferrer">
            Open Google Meet
          </a>
        )}
        {appointment.kind === 'coaching' && context?.active_series && (
          <Typography color="text.secondary">
            {capitalize(appointment.seat_type ?? 'coaching')} coaching ·{' '}
            {context.active_series.remaining_appointment_count} sessions · every{' '}
            {context.active_series.cadence_weeks} weeks
          </Typography>
        )}
        <Typography color="text.secondary">
          Original time: {formatInstant(appointment.current_schedule.start_at, viewerTimezone)}
        </Typography>

        {mode === 'detail' && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {appointment.action_capabilities.reschedule.allowed && (
              <Button variant="contained" onClick={() => setMode('reschedule')}>
                Reschedule
              </Button>
            )}
            {appointment.action_capabilities.cancel.allowed && (
              <Button color="error" variant="outlined" onClick={() => setMode('cancel')}>
                Cancel session
              </Button>
            )}
          </Box>
        )}

        {schedulingContextUnavailable && (
          <Alert severity="error">
            <Typography>Scheduling context could not be loaded.</Typography>
            <Typography variant="body2">
              Retry, or close without making changes. No scheduling changes were made.
            </Typography>
            <Button variant="outlined" onClick={() => schedulingQuery.refetch()} sx={{ mt: 1 }}>
              Retry
            </Button>
          </Alert>
        )}

        {!schedulingContextUnavailable && mode !== 'detail' && scopes.length > 1 && (
          <ScopeChoices
            scopes={scopes}
            scope={effectiveScope}
            onChange={setScope}
            tailSummary={tailSummary(context?.active_series)}
          />
        )}

        {mode === 'reschedule' && !schedulingContextUnavailable && (
          <>
            {schedulingQuery.isLoading && <CircularProgress size={22} />}
            {context?.availability.days[0] && (
              <Typography color="text.secondary">
                {visibleDateRange(context.availability.days[0].date, visibleWeeks)}
              </Typography>
            )}
            {context?.availability.days
              .flatMap((day) => day.slots)
              .map((slot) => (
                <Button
                  key={slot.start_at}
                  variant={selectedStart === slot.start_at ? 'contained' : 'outlined'}
                  onClick={() => setSelectedStart(slot.start_at)}
                >
                  {formatInstant(slot.start_at, context.timezone)}
                </Button>
              ))}
            {context && visibleWeeks < 3 && (
              <Button variant="text" onClick={() => setVisibleWeeks((visibleWeeks + 1) as 2 | 3)}>
                Show More
              </Button>
            )}
            {nearTermCoaching && (
              <Alert severity="warning">
                This session is less than 48 hours away. Confirm immediate participant notice before
                saving.
              </Alert>
            )}
          </>
        )}

        {mode === 'cancel' && !schedulingContextUnavailable && (
          <>
            {appointment.kind === 'chemistry' && (
              <Alert severity="error">
                Chemistry cancellation cannot be undone. Confirm the reason and participant impact.
              </Alert>
            )}
            {appointment.action_capabilities.cancel.initiator_required && (
              <RadioGroup
                aria-label="Cancellation initiator"
                row
                value={initiator}
                onChange={(event) => setInitiator(event.target.value as AdminCancellationInitiator)}
              >
                <FormControlLabel value="member" control={<Radio />} label="Member initiated" />
                <FormControlLabel value="coach" control={<Radio />} label="Coach initiated" />
                <FormControlLabel value="admin" control={<Radio />} label="Ops initiated" />
              </RadioGroup>
            )}
            <TextField
              label="Note to Ops"
              inputProps={{ 'aria-label': 'Note to Ops' }}
              required={appointment.action_capabilities.cancel.ops_note_required}
              value={opsNote}
              onChange={(event) => setOpsNote(event.target.value)}
              multiline
            />
            {appointment.action_capabilities.cancel.reason_required && (
              <TextField
                label="Reason"
                inputProps={{ 'aria-label': 'Reason' }}
                required
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </>
        )}

        {confirmationRequired && mode !== 'detail' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
            }
            label={
              mode === 'reschedule'
                ? 'I understand participants may need immediate notice'
                : 'I confirm this near-term chemistry cancellation'
            }
          />
        )}
        {message && <Alert severity={pending ? 'info' : 'success'}>{message}</Alert>}
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      </DialogContent>
      <DialogActions>
        {mode === 'detail' ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={locked}>
              {confirmedComplete
                ? 'Done'
                : mode === 'reschedule'
                  ? 'Keep current time'
                  : 'Keep session'}
            </Button>
            <Button
              color={mode === 'cancel' ? 'error' : 'primary'}
              variant="contained"
              disabled={
                locked ||
                confirmedComplete ||
                (mode === 'reschedule'
                  ? unchanged || (confirmationRequired && !confirmed) || scopes.length === 0
                  : !cancellationValid) ||
                schedulingContextUnavailable
              }
              onClick={mode === 'reschedule' ? handleReschedule : handleCancel}
            >
              {mode === 'reschedule' ? 'Save changes' : 'Confirm cancellation'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ScopeChoices({
  scopes,
  scope,
  onChange,
  tailSummary,
}: {
  scopes: AppointmentMutationScope[];
  scope: AppointmentMutationScope;
  onChange: (scope: AppointmentMutationScope) => void;
  tailSummary?: string;
}) {
  return (
    <RadioGroup
      value={scope}
      onChange={(event) => onChange(event.target.value as AppointmentMutationScope)}
    >
      {scopes.includes('appointment') && (
        <FormControlLabel
          value="appointment"
          control={<Radio inputProps={{ 'aria-label': 'This session' }} />}
          label="This session"
        />
      )}
      {scopes.includes('this_and_following') && (
        <FormControlLabel
          value="this_and_following"
          control={<Radio inputProps={{ 'aria-label': 'This and following sessions' }} />}
          label={`This and following sessions${tailSummary ? ` (${tailSummary})` : ''}`}
        />
      )}
    </RadioGroup>
  );
}

function dialogTitle(mode: Mode): string {
  if (mode === 'reschedule') return 'Reschedule session';
  if (mode === 'cancel') return 'Cancel session';
  return 'Appointment details';
}

function formatInstant(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(new Date(instant));
}

function tailSummary(
  series:
    | {
        remaining_appointment_count: number;
        affected_end_at: string;
        next_schedule: { timezone: string };
      }
    | null
    | undefined
): string | undefined {
  if (!series) return undefined;
  const through = new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeZone: series.next_schedule.timezone,
  }).format(new Date(series.affected_end_at));
  return `${series.remaining_appointment_count} sessions through ${through}`;
}

function safeScopes(scopes: unknown): AppointmentMutationScope[] {
  if (!Array.isArray(scopes)) return [];
  return scopes.filter(
    (scope): scope is AppointmentMutationScope =>
      scope === 'appointment' || scope === 'this_and_following'
  );
}

function identityLabel(
  role: 'Coach' | 'Member',
  identity: { display_name: string; email: string }
): string {
  return `${role}: ${identity.display_name} (${identity.email})`;
}

function appointmentDurationMinutes(appointment: {
  current_schedule: { start_at: string; end_at: string };
}): number {
  return Math.round(
    (Date.parse(appointment.current_schedule.end_at) -
      Date.parse(appointment.current_schedule.start_at)) /
      60_000
  );
}

function capitalize(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}

function visibleDateRange(firstDate: string, weeks: number): string {
  const start = new Date(`${firstDate}T12:00:00Z`);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + weeks * 7 - 1);
  const startLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(start);
  const endLabel =
    start.getUTCMonth() === end.getUTCMonth()
      ? `${end.getUTCDate()}, ${end.getUTCFullYear()}`
      : new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(end);
  return `Showing ${startLabel}–${endLabel}`;
}

export default AdminAppointmentActionsDialog;
